import {
  Box,
  Button,
  Code,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  Tooltip,
  useClipboard,
  useColorModeValue,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiCopy, FiShield } from 'react-icons/fi'
import QRCode from 'react-qr-code'
import type { AdminMfaSetupResponse } from '@/types'

interface AdminMfaSetupPanelProps {
  setup: AdminMfaSetupResponse
  isLoading: boolean
  onConfirm: (totp: string) => Promise<void>
  onCancel?: () => void
}

export function AdminMfaSetupPanel({
  setup,
  isLoading,
  onConfirm,
  onCancel,
}: AdminMfaSetupPanelProps) {
  const { t } = useTranslation()
  const [totp, setTotp] = useState('')
  const secretClipboard = useClipboard(setup.secret)
  const codeBg = useColorModeValue('gray.100', 'gray.800')

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const normalized = totp.trim()
    if (!/^\d{6}$/.test(normalized)) {
      return
    }
    await onConfirm(normalized)
  }

  return (
    <Stack spacing="5">
      <HStack align="start" spacing="3">
        <Box color="admin.500" pt="0.5">
          <FiShield />
        </Box>
        <Box>
          <Text fontWeight="semibold">{t('auth.mfa.setupTitle')}</Text>
        </Box>
      </HStack>

      <Box bg="white" p="4" alignSelf="center" borderRadius="md">
        <QRCode
          value={setup.qrData}
          size={184}
          aria-label={t('auth.mfa.qrCodeLabel')}
        />
      </Box>

      <Stack spacing="3">
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb="1">
            {t('auth.mfa.setupKey')}
          </Text>
          <HStack>
            <Code
              flex="1"
              px="3"
              py="2"
              bg={codeBg}
              fontSize="sm"
              overflowWrap="anywhere"
            >
              {setup.secret}
            </Code>
            <Tooltip
              label={
                secretClipboard.hasCopied
                  ? t('common.copySuccess')
                  : t('common.copy')
              }
            >
              <IconButton
                aria-label={t('auth.mfa.copySetupKey')}
                icon={<FiCopy />}
                onClick={secretClipboard.onCopy}
                variant="outline"
              />
            </Tooltip>
          </HStack>
        </Box>
      </Stack>

      <form onSubmit={handleSubmit}>
        <Stack spacing="4">
          <FormControl isRequired>
            <FormLabel>{t('auth.mfa.confirmCode')}</FormLabel>
            <Input
              value={totp}
              onChange={(event) => setTotp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={t('auth.mfa.codePlaceholder')}
              maxLength={6}
            />
          </FormControl>
          <HStack justify="flex-end">
            {onCancel && (
              <Button variant="ghost" onClick={onCancel} isDisabled={isLoading}>
                {t('common.cancel')}
              </Button>
            )}
            <Button
              type="submit"
              colorScheme="admin"
              isLoading={isLoading}
              isDisabled={!/^\d{6}$/.test(totp)}
            >
              {t('auth.mfa.confirmSetup')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Stack>
  )
}
